import React, { Suspense, lazy } from 'react';
import { useParams, Link } from 'react-router-dom';
import { PATENT_DEMOS, PATENT_DEMO_ORDER } from '../data/patentDemos';
import { generatePatentDemoPdf } from '../utils/patentPdf';

const P1 = lazy(() => import('../demos/patent/P1_BoardReliability'));
const P2 = lazy(() => import('../demos/patent/P2_EthernetConnectivity'));
const P3 = lazy(() => import('../demos/patent/P3_SupplyChainGraph'));
const P4 = lazy(() => import('../demos/patent/P4_ClosedLoopStability'));
const P5 = lazy(() => import('../demos/patent/P5_LithographyProcessControl'));
const P6 = lazy(() => import('../demos/patent/P6_ThermalOrchestrator'));
const P7 = lazy(() => import('../demos/patent/P7_ProvenanceTrust'));
const P8 = lazy(() => import('../demos/patent/P8_AgentActionGovernance'));
const P9 = lazy(() => import('../demos/patent/P9_ValidationAlignment'));

const COMPONENTS = { P1, P2, P3, P4, P5, P6, P7, P8, P9 };

function DemoLoader() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-blue-300 text-sm font-mono animate-pulse">Loading demo...</div>
    </div>
  );
}

function NarrativeCard({ label, children, tone = 'blue' }) {
  const styles = {
    blue: 'border-blue-500/25 bg-blue-950/30 text-blue-100',
    teal: 'border-teal-500/25 bg-teal-950/20 text-teal-50',
    rose: 'border-rose-500/25 bg-rose-950/20 text-rose-50',
    amber: 'border-amber-500/25 bg-amber-950/20 text-amber-50',
  };
  const labelStyles = {
    blue: 'text-blue-300',
    teal: 'text-teal-300',
    rose: 'text-rose-300',
    amber: 'text-amber-300',
  };
  return (
    <div className={`rounded-xl border p-5 ${styles[tone]}`}>
      <span className={`text-[11px] font-mono uppercase tracking-wider block mb-2 ${labelStyles[tone]}`}>{label}</span>
      <p className="text-sm leading-relaxed">{children}</p>
    </div>
  );
}

export default function PatentDemoPage() {
  const { slug } = useParams();
  const meta = PATENT_DEMOS[slug];

  if (!meta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-white">
        <h1 className="text-2xl font-bold mb-4">Demo Not Found</h1>
        <Link to="/patent-demos" className="text-cyan-400 hover:underline">← Back to all demos</Link>
      </div>
    );
  }

  const DemoComponent = COMPONENTS[meta.componentKey];
  const currentIdx = PATENT_DEMO_ORDER.indexOf(slug);
  const prevSlug = currentIdx > 0 ? PATENT_DEMO_ORDER[currentIdx - 1] : null;
  const nextSlug = currentIdx < PATENT_DEMO_ORDER.length - 1 ? PATENT_DEMO_ORDER[currentIdx + 1] : null;
  const prevMeta = prevSlug ? PATENT_DEMOS[prevSlug] : null;
  const nextMeta = nextSlug ? PATENT_DEMOS[nextSlug] : null;
  const targetBuyers = meta.targetBuyers.split('|');

  return (
    <div className="min-h-screen text-white">
      {/* Back nav */}
      <div className="border-b border-blue-800/30 px-4 py-3">
        <div className="max-w-5xl mx-auto">
          <Link to="/patent-demos" className="text-xs text-blue-300 hover:text-white transition flex items-center gap-1.5">
            ← All Patent Demos
          </Link>
        </div>
      </div>

      {/* Marketing header */}
      <section className="border-b border-blue-800/30 py-10 px-4 bg-blue-950/20">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-wrap gap-2 items-center mb-3">
            <span className="inline-block text-xs font-mono px-2 py-1 bg-blue-500/10 text-blue-300 rounded border border-blue-500/20">{meta.appNum}</span>
            <span className="inline-block text-xs font-mono px-2 py-1 bg-cyan-500/10 text-cyan-200 rounded border border-cyan-500/20">P{meta.index} demo brief</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3">{meta.headline}</h1>
          <p className="text-blue-200 text-lg max-w-3xl">{meta.subheadline}</p>
          <div className="flex flex-wrap gap-3 mt-6">
            <button
              type="button"
              onClick={() => generatePatentDemoPdf(meta)}
              className="px-5 py-2.5 rounded-lg bg-cyan-500/15 border border-cyan-300/50 text-cyan-100 text-sm font-semibold hover:bg-cyan-500/25 transition"
            >
              Export SALAR PDF Brief
            </button>
            <Link to="/contact" className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-500 transition">
              Discuss Pilot / Licensing
            </Link>
          </div>
        </div>
      </section>

      {/* Requirement / definition narrative */}
      <section className="py-10 px-4 border-b border-blue-800/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-mono uppercase tracking-widest text-blue-300 block mb-1">Public-safe explanation</span>
            <h2 className="text-2xl font-bold text-white">What this demo is proving</h2>
            <p className="text-sm text-blue-200 max-w-3xl mx-auto mt-2">
              The page and PDF are written so a customer, reviewer, or partner can understand the requirement, patent definition,
              relevance, good/bad case contrast, and SALAR achievement without needing a long verbal pitch.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <NarrativeCard label="The requirement">{meta.requirement}</NarrativeCard>
            <NarrativeCard label="Patent verbiage definition" tone="teal">{meta.patentDefinition}</NarrativeCard>
            <NarrativeCard label="Relevance to buyers" tone="blue">{meta.relevance}</NarrativeCard>
            <NarrativeCard label="Demo meaning" tone="amber">{meta.demoMeaning}</NarrativeCard>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-10 px-4 border-b border-blue-800/30">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-blue-950/30 rounded-xl border border-rose-500/20 p-6">
            <span className="text-xs font-mono uppercase text-rose-400 tracking-wider block mb-2">The Problem</span>
            <p className="text-blue-100 text-sm leading-relaxed">{meta.problem}</p>
          </div>
          <div className="bg-blue-950/30 rounded-xl border border-teal-500/20 p-6">
            <span className="text-xs font-mono uppercase text-teal-400 tracking-wider block mb-2">The SALAR Method</span>
            <p className="text-blue-100 text-sm leading-relaxed">{meta.solution}</p>
          </div>
        </div>
      </section>

      {/* Best / bad case */}
      <section className="py-10 px-4 border-b border-blue-800/30 bg-blue-950/10">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-xl border border-teal-500/30 bg-teal-950/20 p-6">
              <span className="text-xs font-mono uppercase text-teal-300 tracking-wider block mb-2">Best-version case</span>
              <h3 className="text-lg font-bold text-teal-100 mb-2">{meta.goodCaseTitle}</h3>
              <p className="text-sm leading-relaxed text-teal-50/90">{meta.goodCase}</p>
            </div>
            <div className="rounded-xl border border-rose-500/30 bg-rose-950/20 p-6">
              <span className="text-xs font-mono uppercase text-rose-300 tracking-wider block mb-2">Bad-version case</span>
              <h3 className="text-lg font-bold text-rose-100 mb-2">{meta.badCaseTitle}</h3>
              <p className="text-sm leading-relaxed text-rose-50/90">{meta.badCase}</p>
            </div>
          </div>
          <div className="mt-6 rounded-xl border border-cyan-500/25 bg-cyan-950/20 p-6">
            <span className="text-xs font-mono uppercase text-cyan-300 tracking-wider block mb-2">Achievement statement</span>
            <p className="text-sm leading-relaxed text-blue-50">{meta.achievement}</p>
          </div>
        </div>
      </section>

      {/* Stats + Target buyers */}
      <section className="py-8 px-4 border-b border-blue-800/30 bg-blue-950/10">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-4 items-center justify-between">
          <div className="flex gap-6 flex-wrap">
            {meta.stats.map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-bold text-cyan-300 font-mono">{s.value}</div>
                <div className="text-xs text-blue-300 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            {targetBuyers.map((b, i) => (
              <span key={i} className="text-xs px-3 py-1 bg-blue-900/40 border border-blue-700/40 text-blue-200 rounded-full">{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Demo section */}
      <section className="py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-6">
            <span className="text-xs font-mono uppercase tracking-widest text-blue-300 block mb-1">Interactive Demo</span>
            <h2 className="text-xl font-bold text-white">{meta.title}</h2>
          </div>
          <Suspense fallback={<DemoLoader />}>
            {DemoComponent ? <DemoComponent /> : null}
          </Suspense>
        </div>
      </section>

      {/* Patent nav prev/next */}
      <section className="border-t border-blue-800/30 py-8 px-4 bg-blue-950/20">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between gap-4">
          {prevMeta ? (
            <Link to={`/patent-demos/${prevSlug}`} className="group flex flex-col bg-blue-950/30 border border-blue-800/30 rounded-lg p-4 hover:border-blue-600/50 transition w-full sm:w-auto">
              <span className="text-[10px] font-mono text-blue-400/60 mb-1">← Previous Patent</span>
              <span className="text-sm font-semibold text-blue-100 group-hover:text-white">{prevMeta.title}</span>
            </Link>
          ) : <div />}
          {nextMeta && (
            <Link to={`/patent-demos/${nextSlug}`} className="group flex flex-col bg-blue-950/30 border border-blue-800/30 rounded-lg p-4 hover:border-blue-600/50 transition text-right w-full sm:w-auto ml-auto">
              <span className="text-[10px] font-mono text-blue-400/60 mb-1">Next Patent →</span>
              <span className="text-sm font-semibold text-blue-100 group-hover:text-white">{nextMeta.title}</span>
            </Link>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-blue-800/30 py-10 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h3 className="text-xl font-bold mb-2">Interested in licensing this method?</h3>
          <p className="text-blue-200 text-sm mb-5">Export the SALAR PDF brief, review the public-safe narrative, then contact SALAR to discuss integration, pilot fit, or licensing.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <button type="button" onClick={() => generatePatentDemoPdf(meta)} className="px-5 py-2 bg-cyan-500/15 border border-cyan-400/40 text-cyan-100 rounded-lg text-sm font-semibold hover:bg-cyan-500/25 transition">Export PDF Brief</button>
            <Link to="/licensing" className="px-5 py-2 bg-cyan-500/15 border border-cyan-400/40 text-cyan-100 rounded-lg text-sm font-semibold hover:bg-cyan-500/25 transition">Licensing Info</Link>
            <Link to="/contact" className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-500 transition">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
